/**
 * @solid-analytics/solidstart - Server Functions
 *
 * Server-side analytics tracking with automatic request context enrichment
 */

"use server";

import { getRequestEvent } from "solid-js/web";
import type { AnalyticsInstance } from "analytics";

export interface ServerTrackOptions {
  /**
   * Analytics client instance (must be isomorphic)
   */
  client?: AnalyticsInstance;

  /**
   * Enrich event with request context (IP, user agent, etc.)
   * @default true
   */
  enrichContext?: boolean;

  /**
   * Extract session information from cookies
   * @default true
   */
  includeSession?: boolean;

  /**
   * Debug logging
   * @default false
   */
  debug?: boolean;
}

export interface RequestContext {
  ip?: string;
  userAgent?: string;
  referer?: string;
  country?: string;
  region?: string;
  city?: string;
  sessionId?: string;
  userId?: string;
  [key: string]: any;
}

/**
 * Extract request context from SolidStart request event
 */
function extractRequestContext(options: ServerTrackOptions): RequestContext {
  const event = getRequestEvent();
  if (!event) return {};

  const headers = event.request.headers;
  const context: RequestContext = {};

  // Extract IP address
  context.ip =
    headers.get("x-forwarded-for")?.split(",")[0].trim() ||
    headers.get("x-real-ip") ||
    headers.get("cf-connecting-ip") || // Cloudflare
    "unknown";

  // Extract user agent
  context.userAgent = headers.get("user-agent") || undefined;

  // Extract referer
  context.referer = headers.get("referer") || undefined;

  // Extract geo information (if available from CDN/proxy)
  context.country =
    headers.get("cf-ipcountry") ||
    headers.get("x-vercel-ip-country") ||
    undefined;
  context.region = headers.get("x-vercel-ip-country-region") || undefined;
  context.city = headers.get("x-vercel-ip-city") || undefined;

  // Extract session from cookies if enabled
  if (options.includeSession !== false) {
    const cookieHeader = headers.get("cookie");
    if (cookieHeader) {
      const sessionMatch = cookieHeader.match(/session[_-]?id=([^;]+)/);
      if (sessionMatch) {
        context.sessionId = sessionMatch[1];
      }
      const userMatch = cookieHeader.match(/user[_-]?id=([^;]+)/);
      if (userMatch) {
        context.userId = userMatch[1];
      }
    }
  }

  return context;
}

/**
 * Server-side track function
 *
 * @example
 * ```tsx
 * "use server"
 * import { serverTrack } from '@solid-analytics/solidstart/server'
 *
 * export async function handlePurchase() {
 *   await serverTrack('purchase_completed', { amount: 99.99 })
 * }
 * ```
 */
export async function serverTrack(
  eventName: string,
  properties?: Record<string, any>,
  options: ServerTrackOptions = {}
): Promise<void> {
  const { client, enrichContext = true, debug = false } = options;

  if (!client) {
    if (debug) {
      console.warn("[Analytics] No client provided for server-side tracking");
    }
    return;
  }

  try {
    // Enrich with request context
    let enrichedProperties = properties || {};

    if (enrichContext) {
      const context = extractRequestContext(options);
      enrichedProperties = {
        ...properties,
        $context: context,
        $server: true,
        $timestamp: new Date().toISOString(),
      };
    }

    // Track the event
    await client.track(eventName, enrichedProperties);

    if (debug) {
      console.log(
        `[Analytics] Server tracked: ${eventName}`,
        enrichedProperties
      );
    }
  } catch (error) {
    console.error("[Analytics] Server track error:", error);
    // Don't throw - analytics failures shouldn't break the app
  }
}

/**
 * Server-side identify function
 *
 * @example
 * ```tsx
 * "use server"
 * import { serverIdentify } from '@solid-analytics/solidstart/server'
 *
 * export async function handleLogin(userId: string) {
 *   await serverIdentify(userId, { email: 'user@example.com' })
 * }
 * ```
 */
export async function serverIdentify(
  userId: string,
  traits?: Record<string, any>,
  options: ServerTrackOptions = {}
): Promise<void> {
  const { client, enrichContext = true, debug = false } = options;

  if (!client) {
    if (debug) {
      console.warn("[Analytics] No client provided for server-side identify");
    }
    return;
  }

  try {
    let enrichedTraits = traits || {};

    if (enrichContext) {
      const context = extractRequestContext(options);
      enrichedTraits = {
        ...traits,
        $context: context,
        $server: true,
        $identifiedAt: new Date().toISOString(),
      };
    }

    await client.identify(userId, enrichedTraits);

    if (debug) {
      console.log(`[Analytics] Server identified: ${userId}`, enrichedTraits);
    }
  } catch (error) {
    console.error("[Analytics] Server identify error:", error);
  }
}

/**
 * Server-side page function
 *
 * @example
 * ```tsx
 * "use server"
 * import { serverPage } from '@solid-analytics/solidstart/server'
 *
 * export async function trackPageView() {
 *   await serverPage('Home', { section: 'landing' })
 * }
 * ```
 */
export async function serverPage(
  pageName?: string,
  properties?: Record<string, any>,
  options: ServerTrackOptions = {}
): Promise<void> {
  const { client, enrichContext = true, debug = false } = options;

  if (!client) {
    if (debug) {
      console.warn("[Analytics] No client provided for server-side page");
    }
    return;
  }

  try {
    let enrichedProperties = properties || {};

    if (enrichContext) {
      const event = getRequestEvent();
      const context = extractRequestContext(options);

      enrichedProperties = {
        ...properties,
        $context: context,
        $server: true,
        $url: event?.request.url,
        $path: new URL(event?.request.url || "").pathname,
        $timestamp: new Date().toISOString(),
      };
    }

    await client.page({ title: pageName, ...enrichedProperties });

    if (debug) {
      console.log(
        `[Analytics] Server page: ${pageName || "(none)"}`,
        enrichedProperties
      );
    }
  } catch (error) {
    console.error("[Analytics] Server page error:", error);
  }
}

/**
 * Batch server tracking for multiple events
 *
 * @example
 * ```tsx
 * "use server"
 * import { serverTrackBatch } from '@solid-analytics/solidstart/server'
 *
 * export async function trackUserFlow() {
 *   await serverTrackBatch([
 *     { type: 'track', event: 'page_view' },
 *     { type: 'track', event: 'button_click', properties: { button: 'cta' } },
 *   ])
 * }
 * ```
 */
export async function serverTrackBatch(
  events: Array<{
    type: "track" | "identify" | "page";
    event?: string;
    userId?: string;
    traits?: Record<string, any>;
    properties?: Record<string, any>;
  }>,
  options: ServerTrackOptions = {}
): Promise<void> {
  const promises = events.map(async (evt) => {
    switch (evt.type) {
      case "track":
        if (evt.event) {
          return serverTrack(evt.event, evt.properties, options);
        }
        break;
      case "identify":
        if (evt.userId) {
          return serverIdentify(evt.userId, evt.traits, options);
        }
        break;
      case "page":
        return serverPage(evt.event, evt.properties, options);
    }
  });

  await Promise.allSettled(promises);
}

/**
 * Create a server-side analytics instance with automatic context enrichment
 *
 * @example
 * ```tsx
 * "use server"
 * import { createServerAnalytics } from '@solid-analytics/solidstart/server'
 *
 * const analytics = createServerAnalytics(analyticsClient)
 *
 * export async function trackEvent(name: string, data: any) {
 *   await analytics.track(name, data)
 * }
 * ```
 */
export function createServerAnalytics(
  client: AnalyticsInstance,
  defaultOptions: Omit<ServerTrackOptions, "client"> = {}
) {
  return {
    track: (eventName: string, properties?: Record<string, any>) =>
      serverTrack(eventName, properties, { ...defaultOptions, client }),

    identify: (userId: string, traits?: Record<string, any>) =>
      serverIdentify(userId, traits, { ...defaultOptions, client }),

    page: (pageName?: string, properties?: Record<string, any>) =>
      serverPage(pageName, properties, { ...defaultOptions, client }),

    batch: (events: Parameters<typeof serverTrackBatch>[0]) =>
      serverTrackBatch(events, { ...defaultOptions, client }),
  };
}
